import Ajv from 'ajv'
import { AnalysisConfig } from '../models/AnalysisConfig'
import { AnalysisConfigSchema } from '../models/AnalysisConfigSchema'
import { AnalysisConfigService } from './AnalysisConfigService'
import { destructureWithOmit, recursiveConvertSnakeToCamelCase } from '@/utils'
import { AnalysisConfigTypeGuardService } from './AnalysisConfigTypeGuardService'
import { IQueueExecutionRequest } from '@/shared/Executions/models/IQueueExecutionRequest'
import { CdnSchemaJsonParsingResult } from '@/shared/JsonEditorSchema/models/CdnSchemaJsonParsingResult'
import { SchemaJsonValidationService } from '@/shared/JsonEditorSchema/services/SchemaJsonValidationService'



export class AnalysisConfigJsonParsingResult extends CdnSchemaJsonParsingResult<AnalysisConfig, AnalysisConfigJsonParsingInputType> {

    constructor(
        valid: boolean,
        parsedValue?: any,
        errors: Array<string> = []
    ) {
        super(valid, parsedValue, errors);
        if (valid && parsedValue) {
            // Attempt to extract configs based on input shape
            if ('calculation' in parsedValue && parsedValue?.calculation) {
                this.parsedEntities = [parsedValue.calculation]
                this.inputType = 'full'
            }
            else if (AnalysisConfigTypeGuardService.isAnalysisConfig(parsedValue)) {
                this.parsedEntities = [parsedValue];
                this.inputType = 'flat'
            }
        }
    }

    public withParsedAnalysisConfigs(configs: AnalysisConfig[]): this {
        this.parsedEntities = configs
        return this
    }
}

type IAdvancedAnalysisExecutionPartAnalysisConfigStructure = AnalysisConfig;

export type AnalysisConfigJsonParsingInputType = 'full' | 'flat';

export class AnalysisConfigJsonParsingService extends SchemaJsonValidationService<AnalysisConfig, AnalysisConfigJsonParsingInputType, AnalysisConfigSchema> {

    static readonly injectableKey = 'AnalysisConfigJsonParsingService';

    get injectableKey() {
        return (this.constructor as typeof AnalysisConfigJsonParsingService).injectableKey;
    }

    private _fullConfigsPayloadStructureSchemaValidator =
        new Ajv().compile<IQueueExecutionRequest>({
            type: 'object',
            properties: {
                fileId: {
                    type: 'string'
                },
                name: {
                    type: 'string'
                },
                calculation: {
                    type: 'object',
                    properties: {
                        type: { type: 'string' }
                    },
                    required: ['type'],
                    additionalProperties: true
                }
            },
            required: ['calculation', 'fileId'],
            additionalProperties: true
        })

    private _flatConfigsPayloadStructureSchemaValidator =
        new Ajv().compile<IAdvancedAnalysisExecutionPartAnalysisConfigStructure>({
            type: 'object',
            properties: {
                type: { type: 'string' }
            },
            required: ['type'],
            additionalProperties: true
        })

    // Allows reinitialization of validators with default type value
    public initializeValidators(defaultType?: string) {

        this._fullConfigsPayloadStructureSchemaValidator = new Ajv({ useDefaults: true, allErrors: true }).compile<IQueueExecutionRequest>({
            type: 'object',
            properties: {
                fileId: {
                    type: 'string'
                },
                name: {
                    type: 'string'
                },
                calculation: {
                    type: 'object',
                    properties: {
                        type: { type: 'string', default: defaultType }
                    },
                    required: ['type'],
                    additionalProperties: true
                }
            },
            required: ['calculation', 'fileId'],
            additionalProperties: true
        })

        this._flatConfigsPayloadStructureSchemaValidator = new Ajv({ useDefaults: true, allErrors: true }).compile<IAdvancedAnalysisExecutionPartAnalysisConfigStructure>({
            type: 'object',
            properties: {
                type: { type: 'string', default: defaultType }
            },
            required: ['type'],
            additionalProperties: true
        })
    }

    // Attempts to parse, validate, and initialize analysis config array that can operate with the UI
    public tryParseEntitiesFromJson(value: string, configSchemas: Array<AnalysisConfigSchema>): AnalysisConfigJsonParsingResult {
        if (configSchemas == null || configSchemas.length <= 0) throw new Error('configSchemas is required')

        // Use first configSchema as default if type missing from parsed JSON
        const defaultType = configSchemas[0].type;
        this.initializeValidators(defaultType);

        let result = new AnalysisConfigJsonParsingResult(true)

        if (value == null) {
            result.valid = false
            result.errors.push('Cannot parse JSON from null or empty input value.')
            return result
        }

        try {
            result.parsedValue = JSON.parse(value)
            recursiveConvertSnakeToCamelCase(result.parsedValue)
            return this.tryParseAnalysisConfigs(result, configSchemas)
        }
        catch (error: any) {
            result.valid = false
            result.errors.push(String(error))
        }

        return result
    }

    private tryParseAnalysisConfigs(result: AnalysisConfigJsonParsingResult, configSchemas: Array<AnalysisConfigSchema>): AnalysisConfigJsonParsingResult {
        if (result.parsedValue == null) throw new Error('Invalid configs.parsedValue encountered trying to validateConfigStructure.');

        const parsed = result.parsedValue;
        let analysisConfigs: AnalysisConfig[] | undefined;

        // reset previous Ajv errors
        this._fullConfigsPayloadStructureSchemaValidator.errors = null;
        this._flatConfigsPayloadStructureSchemaValidator.errors = null;
        //this.analysisConfigsArraySchemaValidator.errors = null;

        if (this._fullConfigsPayloadStructureSchemaValidator(parsed)) {
            analysisConfigs = [parsed.calculation];
        } else if (this._flatConfigsPayloadStructureSchemaValidator(parsed)) {
            analysisConfigs = [parsed];
            //} else if (this.analysisConfigsArraySchemaValidator(parsed)) {
            //analysisConfigs = parsed;
        } else {
            const errors = [
                ...(this._fullConfigsPayloadStructureSchemaValidator.errors || []),
                ...(this._flatConfigsPayloadStructureSchemaValidator.errors || []),
                //...(this.analysisConfigsArraySchemaValidator.errors || [])
            ]
                .map((e: any) => `${e.instancePath} ${e.message}`)
                .slice(0, 5);

            return new AnalysisConfigJsonParsingResult(false, undefined, errors)
        }

        try {
            const initialized = analysisConfigs.map(x => this.initializeEntityWithConfiguration(x, configSchemas));
            return new AnalysisConfigJsonParsingResult(true, parsed).withParsedAnalysisConfigs(initialized);
        } catch (err: any) {
            return new AnalysisConfigJsonParsingResult(false, parsed, [`${err.message}`]);
        }
    }

    public initializeEntityWithConfiguration(analysisConfig: AnalysisConfig, analysisConfigSchemas: Array<AnalysisConfigSchema>): AnalysisConfig {
        if (analysisConfig == null) throw new Error('analysisConfig is required');
        if (!analysisConfig.type) throw new Error('analysisConfig.type is required');
        return this.initializeEntity(analysisConfig, analysisConfigSchemas, 1);
    }

    public initializeEntity(analysisConfig: AnalysisConfig, analysisConfigSchemas: Array<AnalysisConfigSchema>, configurationLevel: number): AnalysisConfig {
        let initializedAnalysisConfig = analysisConfig
        const analysisConfigSchema = AnalysisConfigService.tryGetAnalysisConfigSchemaByType(analysisConfig.type, analysisConfigSchemas)
        if (analysisConfigSchema != null) {
            initializedAnalysisConfig = AnalysisConfigService.createAnalysisConfigFromSchema(analysisConfigSchema);
            if (analysisConfig.description != null) initializedAnalysisConfig.description = analysisConfig.description;
            initializedAnalysisConfig._type = analysisConfig.type;
            initializedAnalysisConfig.configuration = destructureWithOmit(analysisConfig, []);
            initializedAnalysisConfig.configuration._type = analysisConfig.type;

            const configRequiresRecursiveInitialization = this.anaylsisConfigRequiresRecursiveInitialization(analysisConfig);
            const configSchemaRequiresRecursiveInitialization = this.analysisConfigSchemaRequiresRecursiveInitialization(analysisConfigSchema);

            if (configurationLevel > 1 && (configRequiresRecursiveInitialization || configSchemaRequiresRecursiveInitialization)) {
                throw new Error('Non top-level analysis configs that contain analysis configs not supported.')
            } else if (configRequiresRecursiveInitialization) {
                throw new Error('AnalysisConfigs containing child analysis configs is not supported.')
            }
        }
        return initializedAnalysisConfig
    }

    private anaylsisConfigRequiresRecursiveInitialization(analysisConfig: AnalysisConfig): boolean {
        return AnalysisConfigTypeGuardService.supportsChildAnalysisConfigs(analysisConfig)
    }

    private analysisConfigSchemaRequiresRecursiveInitialization(analysisConfigSchema: AnalysisConfigSchema): boolean {
        return AnalysisConfigTypeGuardService.supportsChildAnalysisConfigs(analysisConfigSchema)
    }
}
